import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import GenericSelect from '../../../components/ui/GenericSelect';
import Button from '../../../components/ui/Button';
import { formatDate } from '../../../utils/Utils';
import { usePanel } from '../../performance-operations/config';
import { FilterField, FilterRow, ListWrap } from '../styles';
import { PIP_STATUSES, fetchPips } from '../config';

// Analiz E12 alt alan (PIP sekmesi) — çalışan, amaç (hedefler), başlangıç, bitiş, sorumlu, tamamlanma (ulaşılan hedef), kanıt
// (ara görüşme notu sayısı), kontrol görüşmesi (son ara görüşme) ve kapanış sonucu. Satır → PIP detayı (mevcut PIP Süreçleri
// sayfası, ?pipId= ile çekmece açılır). PIP başlatma/kapanış kararı bu ekranda yok (insan onayı mevcut akışta).
const statusLabel = (t, s) => (s === 'draft'
  ? t('performance.developmentAnalytics.pip.pendingReview')
  : t(`performance.pip.status.${s}`, { defaultValue: s }));

export default function PipListTable({ filters, closeWindow, statuses, onStatuses }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const [paging, setPaging] = useState({ pageNumber: 1, pageSize: 10 });
  const key = JSON.stringify({ filters, closeWindow, statuses });
  const [lastKey, setLastKey] = useState(key);
  if (lastKey !== key) { setLastKey(key); setPaging((p) => ({ ...p, pageNumber: 1 })); }
  const list = usePanel(() => fetchPips(filters, closeWindow, statuses, paging.pageNumber, paging.pageSize),
    [key, paging.pageNumber, paging.pageSize], !!filters.periodId);
  const rows = useMemo(() => list.data?.rows || [], [list.data]);

  const columns = useMemo(() => [
    { name: 'employeeName', label: t('performance.developmentAnalytics.col.employee'), options: { filter: false, sort: false } },
    {
      name: 'firstGoalTitle', label: t('performance.developmentAnalytics.col.goal'),
      options: {
        filter: false, sort: false,
        customBodyRenderLite: (i) => {
          const r = rows[i];
          if (!r) return '-';
          if (!r.firstGoalTitle) return '-';
          return r.goalCount > 1 ? `${r.firstGoalTitle} +${r.goalCount - 1}` : r.firstGoalTitle;
        },
      },
    },
    { name: 'startedAt', label: t('performance.developmentAnalytics.col.start'), options: { filter: false, sort: false, customBodyRender: (v) => formatDate(v, lang) } },
    {
      name: 'dueDate', label: t('performance.developmentAnalytics.col.end'),
      options: { filter: false, sort: false, customBodyRenderLite: (i) => formatDate(rows[i]?.closedAt || rows[i]?.dueDate, lang) },
    },
    { name: 'ownerName', label: t('performance.developmentAnalytics.col.owner'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    {
      name: 'achievedGoals', label: t('performance.developmentAnalytics.col.completion'),
      options: { filter: false, sort: false, customBodyRenderLite: (i) => (rows[i] ? `${rows[i].achievedGoals}/${rows[i].goalCount}` : '-') },
    },
    { name: 'checkInCount', label: t('performance.developmentAnalytics.col.evidence'), options: { filter: false, sort: false } },
    { name: 'lastCheckInAt', label: t('performance.developmentAnalytics.col.checkIn'), options: { filter: false, sort: false, customBodyRender: (v) => formatDate(v, lang) } },
    { name: 'status', label: t('performance.developmentAnalytics.col.outcome'), options: { filter: false, sort: false, customBodyRender: (v) => statusLabel(t, v) } },
    {
      name: 'pipId', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false, viewColumns: false,
        customBodyRenderLite: (i) => {
          const r = rows[i];
          if (!r) return null;
          return (
            <OptionsMenu
              row={r}
              actions={[{ type: 'detail', label: t('performance.developmentAnalytics.action.openPip'), onClick: () => history.push(`/performance/pips?pipId=${r.pipId}`) }]}
            />
          );
        },
      },
    },
  ], [t, lang, rows, history]);

  const onTableChanged = useCallback((action, state) => {
    if (action === 'changePage' || action === 'changeRowsPerPage') setPaging({ pageNumber: (state.page || 0) + 1, pageSize: state.rowsPerPage || 10 });
  }, []);

  return (
    <ListWrap data-panel="pips">
      <FilterRow>
        <FilterField>
          <GenericSelect
            label={t('performance.developmentAnalytics.filter.status')}
            value={statuses?.[0] || null}
            onChange={(v) => onStatuses(v ? [v] : [])}
            options={PIP_STATUSES.map((s) => ({ id: s, name: statusLabel(t, s) }))}
          />
        </FilterField>
      </FilterRow>
      <DataTable
        title={t('performance.developmentAnalytics.list.pips')}
        fetching={list.loading}
        columns={columns}
        data={list.error ? [] : rows}
        totalRowCount={list.data?.total || 0}
        pageIndex={paging.pageNumber - 1}
        pageSize={paging.pageSize}
        onTableChanged={onTableChanged}
        disableFilters
        tableBodyMaxHeight="none"
      />
      {list.error ? <Button variant="outline" onClick={list.retry} data-action="retry-pips">{t('performance.operations.panel.retry')}</Button> : null}
    </ListWrap>
  );
}
